import React, { useState, useRef, useEffect } from "react";
import "./SearchableDropDown.scss";
import AuiIcon from "../AuiDropdown/AuiIcon";

export interface Props {
  dropdownLabel?: string;
  placeHolder?: string;
  required?: boolean;
  errorText?: string;
  onChange?: (selectedValues: { label: string; value: string }[]) => void;
  data?: { label: string; value: string }[];
  selectedOptions?: { label: string; value: string }[];
  value?: string;
  isLoading?: boolean;
  onSearch?: (term: string) => void;
  dropId: string
}

const SearchableMultiSelectDropdown = ({
  dropdownLabel,
  data = [],
  placeHolder = "Select Values...",
  required = false,
  errorText = "",
  onChange,
  selectedOptions,
  value: initialValue,
  isLoading,
  onSearch,
  dropId
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedValues, setSelectedValues] = useState<
    { label: string; value: string }[]
  >(selectedOptions || []);
  const [error, setError] = useState("");
  const [value, setValue] = useState<string | undefined>(initialValue);
  const [touched, setTouched] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [liveMessage, setLiveMessage] = useState("");

  useEffect(() => {
    if (selectedOptions) {
      setSelectedValues(selectedOptions);
    }
  }, [selectedOptions]);

  useEffect(() => {
    if (touched) {
      setError(selectedValues.length === 0 && required ? errorText : "");
    }
  }, [selectedValues, required, errorText, touched]);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleBlur = () => {
    setTouched(true);
    validateField();
  };

  useEffect(() => {
    if (searchInputRef.current) {
      setTimeout(() => {
        const length = searchInputRef.current!.value.length;
        searchInputRef.current!.setSelectionRange(length, length);
      }, 0);
    }
  }, [searchTerm]);

  const handleToggle = () => {
    setOpen((prev) => {
    const newState = !prev;
        if(!newState) {
          setSearchTerm("");
          onSearch?.("");
        }
      return newState;
    });
    setTouched(true);
    if (!open) {
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          const length = searchInputRef.current.value.length;
          searchInputRef.current.setSelectionRange(length, length);
        }
      }, 0);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (ref.current && !ref.current.contains(event.target as Node)) {
      setOpen(false);
      setSearchTerm("");
      onSearch?.("");
      setActiveIndex(-1);
    }
  };

  useEffect(() => {
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTerm = e.target.value;
    setSearchTerm(newTerm);
    onSearch?.(newTerm);
    setActiveIndex(-1);
  };

  const handleSelect = (value: string) => {
    setTouched(true);
    const selectedItem = data.find((item) => item.value === value);
    if (!selectedItem) return;

    setSelectedValues((prev) => {
      const exists = prev.some((v) => v.value === value);
      const newSelection = exists
        ? prev.filter((v) => v.value !== value)
        : [...prev, selectedItem];

      onChange?.(newSelection);
      setLiveMessage(`${selectedItem.label} ${exists ? "unselected" : "selected"}`);
      setTimeout(() => searchInputRef.current?.focus(), 0);
      return newSelection;
    });
  };

  const mergedData = [
    ...data,
    ...selectedValues.filter(
      (sel) => !data.find((item) => item.value === sel.value)
    ),
  ];

  const filteredData = mergedData?.filter((item) =>
  (item.label || "").toLowerCase().includes(searchTerm.trim().toLowerCase())
);

  const validateField = () => {
    if (touched && selectedValues.length === 0) {
      setError(errorText);
    } else {
      setError("");
    }
  };

  const escapeRegExp = (text: string) => {
    return text.replace(/[-\\^$*+?.()|[\]{}]/g, "\\$&");
  };

  const getHighlightedText = (text: string, highlight: string) => {
    if (!highlight) return text;

    const regex = new RegExp(`(${escapeRegExp(highlight)})`, "gi");
    const parts = text.split(regex);

    let highlightCount = 0;

    return (
      <>
        {parts.map((part, index) => {
          if (
            part.toLowerCase() === highlight.toLowerCase() &&
            highlightCount === 0
          ) {
            highlightCount++;
            return (
              <span key={part || index} className="highlight">
                {part}
              </span>
            );
          }
          return part;
        })}
      </>
    );
  };

    const scrollIntoView = (index: number) => {
    const el = document.getElementById(`dropdown-item-${index}`);
    el?.scrollIntoView({ block: "nearest" });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === " ") {
      e.stopPropagation();
      e.preventDefault();
      setSearchTerm((prev) => prev + " ");
    }
    if (e.key === "Escape") {
      setOpen(false);
      searchInputRef?.current?.focus();
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => {
        const next = prev + 1 >= filteredData.length ? 0 : prev + 1;
        scrollIntoView(next);
        return next;
      });
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => {
        const next = prev - 1 < 0 ? filteredData.length - 1 : prev - 1;
        scrollIntoView(next);
        return next;
      });
    }

    if (e.key === "Tab") {
        setOpen(false);
        setActiveIndex(0);
        return;
      }

    if (e.key === "Enter" && activeIndex >= 0 && activeIndex < filteredData.length) {
      e.preventDefault();
      handleSelect(filteredData[activeIndex].value);
    }
  };

  const renderInputData = (): JSX.Element => {
    if (open) {
      const inputClass = `search-input ${
        selectedValues.length > 0 ? "relative-position" : ""
      }`;
      const inputPlaceholder = selectedValues.length < 1 ? "Search..." : "";

      return (
        <input
          ref={searchInputRef}
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          className={inputClass}
          placeholder={inputPlaceholder}
          onClick={(e) => e.stopPropagation()}
          role="combobox"
          aria-autocomplete="list"
          aria-controls="dropdown-list"
          aria-expanded={open}
          aria-activedescendant={
            activeIndex >= 0 ? `dropdown-item-${activeIndex}` : undefined
          }
        />
      );
    }

    const hasValue = value && value !== "";
    const showPlaceholder = !hasValue && selectedValues.length < 1;
    let displayText = "";
    if (hasValue) {
      displayText = value;
    } else if (showPlaceholder) {
      displayText = placeHolder;
    }

    return (
      <div className="placeholdText" title={displayText}>
        {displayText}
        {isLoading && !hasValue && (
          <span className="loader-spinner" data-testid="dropdown-loader" />
        )}
      </div>
    );
  };

  let suggestionText = "";
  if (filteredData.length === 0) {
    suggestionText = "No data found";
  } else {
    const addSuffix = filteredData.length > 1 ? "s" : "";
    suggestionText = `Updated: ${filteredData.length} suggestion${addSuffix} for '${searchTerm}'`;
  }

  return (
    <div className="dropdown-wrapper" ref={ref}>
      {dropdownLabel && (
        <label id={`${dropId}-label`}>
          {dropdownLabel}
          {required && (
            <>
              <span className="visually-hidden">&nbsp;required</span>
              <span className="requiredLabel" aria-hidden="true">
                *
              </span>
            </>
          )}
        </label>
      )}
      <div className="dropdownText multi-select">
        <div
          className="dropDown"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={handleToggle}
          onBlur={handleBlur}
          tabIndex={0}
          aria-labelledby={`${dropId}-label`}
          aria-describedby={error ? `${dropId}-error` : undefined}
          aria-required={required}
          aria-controls="listbox-id"
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handleToggle();
              setOpen(true);
            }
          }}
        >
          <div className="input-container">
            {selectedValues.length > 0 && (
              <div
                className="selectedLabel"
                title={
                  selectedValues.length === 1
                    ? selectedValues[0].label
                    : `${selectedValues.length} selected`
                }
              >
                <span className="selected-values">
                  {selectedValues.length === 1
                    ? selectedValues[0].label
                    : `${selectedValues.length} selected`}
                </span>
                <button
                  type="button"
                  className="clearSelection"
                  aria-label={`Clear selected ${dropdownLabel ?? 'option'}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSearch?.("");
                    onChange?.([]);
                    validateField();
                  }}
                >
                  <AuiIcon iconClass="closeIcon" svgheight="8" svgwidth="8" />
                </button>
              </div>
            )}
            {renderInputData()}
          </div>
          {searchTerm && open && (
            <button
              className="close-icon"
              onClick={(e) => {
                e.stopPropagation();
                setSearchTerm("");
                onSearch?.("");
                validateField();
              }}
              data-testid="clear-search-id"
              aria-label={`Clear ${dropdownLabel} option`}
            >
              <AuiIcon iconClass="closeIcon" svgheight="12" svgwidth="12" />
            </button>
          )}
        </div>
        <button className="dropicon" onClick={handleToggle} tabIndex={-1}>
          <AuiIcon
            iconClass={open ? "arrowup" : "arrowdown"}
            svgheight="20"
            svgwidth="20"
          />
        </button>
        {error && (
          <span className="dropError" id={`${dropId}-error`}>
            {error}
          </span>
        )}
        {open && (
          <div className="dropContent">
            <div role="listbox"
                id="dropdown-list"  className="multi-select-dropdown-list">
              {filteredData.length > 0 ? (
                filteredData.map((item, index) => {
                const isSelected = selectedValues.some((val) => val.value === item.value);
                return (
                  <div
                    className="li"
                    key={item.value || index}
                    id={`dropdown-item-${index}`}
                    role="option"
                    aria-selected={activeIndex === index}
                    tabIndex={-1}
                  >
                    <button
                      onClick={() => handleSelect(item.value)}
                      className={activeIndex === index ? "itemActive" : ""}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        readOnly
                      />
                      <label className="checkbox-label-text" title={item.label}>
                        {getHighlightedText(item.label || "", searchTerm)}
                      </label>
                    </button>
                  </div>
                );
              })
              ) : (
                <div className="no-results">No data found</div>
              )}
            </div>
            <div aria-live="polite" className="visually-hidden">
              {liveMessage}
            </div>
            <output
              id={`${dropId}-output`}
              className="visually-hidden"
              aria-live="polite"
              aria-atomic="true"
              aria-relevant="all"
            >
              {suggestionText}
            </output>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchableMultiSelectDropdown;
